import {
  Text,
  TokenTextAppearance,
  styled,
} from '@volkswagen-onehub/components-core';
import React, {Fragment, useEffect} from 'react';
import CarImage from './car_image';
import { Model, Trim } from '../types/data';

const CarModelContainer = styled.div`
  margin-top: 2rem;
  margin-bottom: 2rem;

  display: flex;
  align-items: center;
  flex-direction: column;
  column-gap: 2rem;

  img {
    width: 100%;
    max-width: 400px;
  }

  @media screen and (min-width: 560px) {
    flex-direction: row;
    margin-top: 5rem;
    margin-bottom: 5rem;
  }
`;

const CarTrimContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const TableContainer = styled.div`
  overflow-x: auto;

  &.hidden {
    display: none;
  }

  .table-model {
    background: #fff;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  .table__header-item {
    background-color: $white;
    padding: 16px;
    min-width: 100px;
    font-size: 16px;
    line-height: 24px;
    text-align: left;
    height: 105px;
    box-sizing: border-box;
    width: 14%;
    vertical-align: bottom;
    background-clip: padding-box;

    &:first-child {
      padding-left: 10px;
      width: 15%;
      position: sticky;
      z-index: 1;
      left: 0;
    }

    &:last-child {
      width: 15%;
    }
  }

  .table__distance-header-label {
    font-size: 11px;
    display: block;
    margin-top: 10px;
  }

  .table__row {
    height: 105px;
    font-size: 16px;
    line-height: 24px;
    text-align: left;
    background-color: $white;
    border-bottom: solid 1px #dfe4e8;

    &--header {
      border-bottom: solid 1px;
    }
  }

  .table__col {
    padding: 16px;
    vertical-align: bottom;
    background-color: $white;
    position: relative;
    background-clip: padding-box;

    &:first-child {
      position: sticky;
      z-index: 1;
      left: 0;
      padding: 0;
      background-color: $white;
    }
    &.highlight {
      font-weight: bold;
      background-color: rgba(0, 67, 122, 0.08);
    }
  }

  .table__element {
    padding: 16px;
    min-height: 105px;
    height: 100%;
    display: flex;
    align-items: flex-end;
    box-sizing: border-box;
    &.highlight {
      background-color: rgba(0, 67, 122, 0.08);
    }
  }

  .table__car-name {
    margin: 0;
  }
`;

const TableCaptionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: solid 2px;
  align-items: center;
  padding-bottom: 10px;
`;

type Props = {
  labels: any;
  selectedCar: any;
  trim: any;
  language: string;
  tableSource: Array<any>;
  distance: number;
  tracking: any;
  isShowroomPage: boolean;
  trimDetails: Trim;
};

const ComparationTable = (props: Props) => {
  const {
    labels,
    tableSource,
    language,
    selectedCar,
    trim,
    distance,
    tracking,
    isShowroomPage,
    trimDetails
  } = props;

  const _format_price = (price: string) => {
    return new Intl.NumberFormat(`${language}-CA`, {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(parseInt(price));
  };

  const getColKey = (car: any, rowIndex: number, colIndex: number) => {
    return `${car.Model}-${rowIndex}-${colIndex}`;
  };

  const _renderComparation = () => {
    return tableSource
      .map((car, index) => {
        let rowKey = `${car.Model}-${index}-row`;
        let highlight =
          car.Model.toLowerCase() === selectedCar.name.toLowerCase()
            ? 'highlight'
            : '';
        return (
          <tr className="table__row" key={rowKey}>
            <td
              className={`table__col table-model`}
              key={getColKey(car, index, 1)}
            >
              <div className={`table__element ${highlight}`}>
                <p className="table__car-name">
                  {language === 'en'
                    ? `${car.Year} ${car.Make} ${car.Model} ${car.Series}`
                    : `${car.Make} ${car.Model} ${car.Year} ${car.Series}`}
                </p>
              </div>
            </td>
            <td
              className={`table__col ${highlight}`}
              key={getColKey(car, index, 2)}
            >
              <div className="">{_format_price(car.year1)}</div>
            </td>
            <td
              className={`table__col ${highlight}`}
              key={getColKey(car, index, 3)}
            >
              <div className="">{_format_price(car.year2)}</div>
            </td>
            <td
              className={`table__col ${highlight}`}
              key={getColKey(car, index, 4)}
            >
              <div className="">{_format_price(car.year3)}</div>
            </td>
            <td
              className={`table__col ${highlight}`}
              key={getColKey(car, index, 5)}
            >
              <div className="">{_format_price(car.year4)}</div>
            </td>
            <td
              className={`table__col ${highlight}`}
              key={getColKey(car, index, 6)}
            >
              <div className="">{_format_price(car.year5)}</div>
            </td>
            <td
              className={`table__col ${highlight}`}
              key={getColKey(car, index, 7)}
            >
              <div className="">{_format_price(car.total)}</div>
            </td>
          </tr>
        );
      })
      .sort((a: any, b: any) => (a.total > b.total ? 1 : -1));
  };

  const _getDistanceYear = (year: number) => {
    const formattedDistance = new Intl.NumberFormat(`${language}-CA`).format(
      distance * year,
    );
    return labels.distance.replace('{distance}', formattedDistance);
  };

  useEffect(() => {
    if (tableSource.length > 0) {
      let filterlist;
      if (isShowroomPage) {
        filterlist = [
          {
            type: 'single_value',
            name: 'TR',
            values: [trim],
          },
          {
            type: 'single_value',
            name: 'DI',
            values: [distance],
          },
        ];
      } else {
        filterlist = [
          {
            type: 'single_value',
            name: 'CG',
            values: [`${selectedCar.year} ${selectedCar.name}`],
          },
          {
            type: 'single_value',
            name: 'TR',
            values: [trim],
          },
          {
            type: 'single_value',
            name: 'DI',
            values: [distance],
          },
        ];
      }
      const data = {
        environment: {
          featureAppId: 'CostOfOwnership-D6-CA',
        },
        filter: {
          results: undefined,
          numberOfResults: tableSource.length,
          filterlist,
          sortOrder: [
            {
              sortby: 'Total maintenance cost',
              order: 'asc',
            },
          ],
        },
      };
      tracking.track('VWBasic_FilterSearchResult_Load', data);
    }
  }, [tableSource, distance, isShowroomPage, selectedCar, tracking, trim]);

  return (
    <Fragment>
      <CarModelContainer>

      <CarImage trimDetails={trimDetails} modelName={(selectedCar as Model).name} modelYear={(selectedCar as Model).year} />

        <CarTrimContainer>
          <Text appearance={TokenTextAppearance.headline400} bold>
            {language === 'en'
              ? `${selectedCar.year} ${selectedCar.name}`
              : `${selectedCar.name} ${selectedCar.year}`}
          </Text>
          <Text appearance={TokenTextAppearance.headline400} bold>
            {trim}
          </Text>
        </CarTrimContainer>
      </CarModelContainer>

      <TableCaptionContainer>
        <Text bold appearance={TokenTextAppearance.headline100}>
          {labels.table_headline}
        </Text>

        {/* <Text appearance={TokenTextAppearance.copy100}>
					<CTA tag="button" icon={<Printer ariaHidden ariaLabel='Print Icon'/>} emphasis='secondary' onClick={() => onPrint()}>{labels.print}</CTA>
				</Text> */}
      </TableCaptionContainer>

      <TableContainer>
        <Table className="table" aria-describedby="tableDesc">
          <thead>
            <tr className="table__row table__row--header">
              <th className="table__header-item table-model">
                {labels.compact_models}
              </th>
              <th className="table__header-item">
                <span>{labels.main_cost_1}</span>
                <span className="table__distance-header-label">
                  {_getDistanceYear(1)}
                </span>
              </th>
              <th className="table__header-item">
                {labels.main_cost_2}
                <span className="table__distance-header-label">
                  {_getDistanceYear(2)}
                </span>
              </th>
              <th className="table__header-item">
                {labels.main_cost_3}
                <span className="table__distance-header-label">
                  {_getDistanceYear(3)}
                </span>
              </th>
              <th className="table__header-item">
                {labels.main_cost_4}
                <span className="table__distance-header-label">
                  {_getDistanceYear(4)}
                </span>
              </th>
              <th className="table__header-item">
                {labels.main_cost_5}
                <span className="table__distance-header-label">
                  {_getDistanceYear(5)}
                </span>
              </th>
              <th className="table__header-item">{labels.main_cost_total}</th>
            </tr>
          </thead>
          <tbody className="table__body">{_renderComparation()}</tbody>
        </Table>
      </TableContainer>
    </Fragment>
  );
};

export default ComparationTable;
